// import { useQueries } from '@tanstack/react-query'

import { useQueries } from '@tanstack/vue-query'
import { computed } from 'vue'

import { getUserData, V1_AUTH_USER_QUERY_KEY, V1_USER_DATA_QUERY_KEY, V1GetAuthUser } from '@/features/auth/api'
import useIsAuth from '@/features/auth/hooks/use-is-auth.hook'
import type { V1AccountDataType, V1AuthUserType, V2AccountDataType } from '@/features/auth/types'
import logout from '@/features/auth/utils/logout'

function V1ToV2DataMiddleware(
  data?: Partial<V1AccountDataType & V1AuthUserType>,
): Partial<V2AccountDataType> | undefined {
  if (!data) return undefined

  return {
    email: data?.email,
    firstName: data?.identity?.firstname,
    lastName: data?.identity?.lastname,
    id: data?.id,
    picture: data?.personalInformation?.profilPicture,
    supportId: data.supportId,
    hasSAML: !!data.samlId,
    // KC-537 : à jour = dernière acceptation de chaque document à la version
    // courante (calculé côté API). Fallback sur le booléen legacy.
    hasAcceptedTerms: data.termsUpToDate ?? data.cguAccepted,
    termsReacceptance: data.termsReacceptance ?? false,
    termsAcceptedAt: data.termsAcceptedAt ?? null,
    legalDocVersions: data.legalDocVersions ?? null,
    language: data?.language,
    mfaEnabled: data.mfaEnabled || false,
    isConstelStaff: !!data.email?.trim().toLowerCase().endsWith('@constel-education.com'),
  }
}

function useAccount() {
  const { isAuthRef } = useIsAuth()

  const queryResult = useQueries({
    queries: [
      {
        queryKey: [V1_USER_DATA_QUERY_KEY],
        queryFn: getUserData,
        enabled: () => isAuthRef.value,
      },
      {
        queryKey: [V1_AUTH_USER_QUERY_KEY],
        queryFn: async () => {
          try {
            const response: any = await V1GetAuthUser()
            return response
          } catch (error: any) {
            if (error.message == 'Access denied: invalid token') {
              return logout()
            }
          }
        },
        enabled: () => isAuthRef.value,
      },
    ],
  })

  // merging all data from the queries (that constitute the account data)
  const accountDataRef = computed(() => {
    const userData = queryResult.value[0].data
    const authUserData = queryResult.value[1].data
    return V1ToV2DataMiddleware({
      ...userData,
      ...authUserData,
    })
  })

  // check if any query is pending
  const isPendingRef = computed(() => queryResult.value.some((query) => query.isFetching))

  return {
    accountRef: accountDataRef,
    isPendingRef,
  }
}

export { V1_AUTH_USER_QUERY_KEY, V1_USER_DATA_QUERY_KEY }
export default useAccount
